import { useCallback, useEffect, useState } from 'react';
import { Bot, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface McpZugang {
  id: number;
  name: string;
  tokenAnfang: string;
  createdAt: string | null;
  zuletztBenutztAm: string | null;
  /** "anmeldung": über Claude verbunden (OAuth), "token": selbst angelegtes Token. */
  art?: 'anmeldung' | 'token';
}

type NeuerZugang = McpZugang & { token: string };

const datum = (wert: string | null) => (wert ? new Date(wert).toLocaleString('de-DE') : '—');

/**
 * KI-Verbindungen (MCP, docs/mcp-konzept.md). Der einfache Weg: In Claude die Adresse
 * als Konnektor eintragen und hier im Office zulassen (OAuth, /ki-freigabe) — die
 * Verbindung steht dann in dieser Liste. Für Claude Code geht zusätzlich ein selbst
 * angelegtes Token; es steht nur direkt nach dem Anlegen da, gespeichert ist der Hash.
 * Wer die Grundberechtigung nicht hat, bekommt 403 — dann bleibt die Karte weg.
 */
const McpZugaengeCard = () => {
  const [zugaenge, setZugaenge] = useState<McpZugang[] | null>(null);
  const [name, setName] = useState('');
  const [neu, setNeu] = useState<NeuerZugang | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(async () => {
    try {
      const res = await api.get<{ items: McpZugang[] }>('/api/mcp/zugaenge');
      setZugaenge(Array.isArray(res?.items) ? res.items : []);
    } catch {
      setZugaenge(null);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  if (zugaenge === null) return null;

  const endpunkt = `${window.location.origin}/api/mcp`;
  const befehl = neu
    ? `claude mcp add --transport http sidigo ${endpunkt} --header "Authorization: Bearer ${neu.token}"`
    : '';

  const anlegen = async () => {
    setLaeuft(true);
    try {
      const angelegt = await api.post<NeuerZugang>('/api/mcp/zugaenge', { name: name.trim() || 'Claude' });
      setNeu(angelegt);
      setName('');
      await laden();
    } catch (e) {
      toast.error(e instanceof Error && e.message ? e.message : 'Zugang konnte nicht angelegt werden');
    } finally {
      setLaeuft(false);
    }
  };

  const widerrufen = async (zugang: McpZugang) => {
    try {
      await api.delete(`/api/mcp/zugaenge/${zugang.id}`);
      if (neu?.id === zugang.id) setNeu(null);
      toast.success('Zugang widerrufen');
      await laden();
    } catch {
      toast.error('Zugang konnte nicht widerrufen werden');
    }
  };

  const kopieren = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Kopiert');
    } catch {
      toast.error('Kopieren ging nicht — bitte von Hand markieren');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Bot className="h-5 w-5" /> KI-Verbindung (Claude)</CardTitle>
        <CardDescription>
          Damit kann Claude Sidigo in deinem Namen lesen: Personen finden, Verträge und Stundenpläne
          ansehen. Es gelten genau deine Rechte. Ändern kann die Verbindung nichts.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 rounded-md border p-3">
          <p className="text-sm font-medium">So verbindest du Claude</p>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>In Claude (App oder claude.ai): Einstellungen › Konnektoren › Benutzerdefinierten Konnektor hinzufügen.</li>
            <li>Name „Sidigo“, als Adresse diese hier eintragen:</li>
          </ol>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-muted px-2 py-1 text-xs">{endpunkt}</code>
            <Button variant="outline" size="sm" onClick={() => void kopieren(endpunkt)} aria-label="Adresse kopieren">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Dann auf „Verbinden“ klicken und im Sidigo-Fenster „Zulassen“ wählen. Die Verbindung erscheint unten.
          </p>
        </div>

        <p className="text-xs text-muted-foreground">Für Claude Code geht auch ein eigenes Token:</p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="grid flex-1 gap-1.5">
            <Label htmlFor="mcpZugangName">Name des Zugangs</Label>
            <Input
              id="mcpZugangName"
              value={name}
              maxLength={120}
              placeholder="z. B. Claude auf dem Büro-Laptop"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <Button onClick={() => void anlegen()} disabled={laeuft}>Zugang anlegen</Button>
        </div>

        {neu && (
          <div className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-3">
            <p className="text-sm font-medium">Dein Token — es wird nur jetzt angezeigt:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded bg-muted px-2 py-1 text-xs">{neu.token}</code>
              <Button variant="outline" size="sm" onClick={() => void kopieren(neu.token)} aria-label="Token kopieren">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Für Claude Code im Terminal:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded bg-muted px-2 py-1 text-xs">{befehl}</code>
              <Button variant="outline" size="sm" onClick={() => void kopieren(befehl)} aria-label="Befehl kopieren">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {zugaenge.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Verbindung.</p>
        ) : (
          zugaenge.map((z) => (
            <div key={z.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
              <div className="space-y-1">
                <p className="text-sm font-medium">{z.name}</p>
                <p className="text-xs text-muted-foreground">
                  {z.art === 'anmeldung' ? 'über Claude verbunden' : `${z.tokenAnfang}…`} · angelegt {datum(z.createdAt)} · zuletzt benutzt {datum(z.zuletztBenutztAm)}
                </p>
              </div>
              <Button variant="destructive" size="sm" onClick={() => void widerrufen(z)}>
                Widerrufen
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default McpZugaengeCard;
